/**
 * Dictionnaire complet en Français pour l'application Ma Facture.
 */

export const en = {
  brand: {
    name: 'Ma Facture',
    tagline: 'Transformez vos factures en données exploitables en un clin d\'œil.',
  },
  common: {
    loading: 'Chargement…',
    error: 'Une erreur est survenue',
    retry: 'Réessayer',
    save: 'Enregistrer',
    cancel: 'Annuler',
    confirm: 'Confirmer',
    delete: 'Supprimer',
    edit: 'Modifier',
    yes: 'Oui',
    no: 'Non',
    next: 'Suivant',
    back: 'Retour',
  },
  landing: {
    eyebrow: 'OCR + Traitement Intelligent de Factures',
    hero: {
      title: 'Arrêtez de retaper vos factures.',
      titleAccent: 'Déposez-les simplement.',
      lede: 'Ma Facture extrait automatiquement fournisseurs, clients, articles, totaux et taxes depuis n\'importe quelle facture (PDF, image, Word, Excel) et les exporte vers JSON, UBL, Factur-X, XLSX, CSV ou PDF.',
      ctaPrimary: 'Commencer gratuitement',
      ctaSecondary: 'Voir le fonctionnement',
      trustFiles: 'formats acceptés',
      trustExports: 'formats d\'export',
      trustHosting: '100% sécurisé & local',
    },
    how: {
      title: 'Comment ça marche',
      step1: { num: '01', title: 'Téléversez', desc: 'Glissez un PDF, une image ou une feuille de calcul. Jusqu\'à 25 Mo par fichier.' },
      step2: { num: '02', title: 'Extraction IA', desc: 'L\'OCR haute précision et les algorithmes intelligents extraient chaque champ instantanément.' },
      step3: { num: '03', title: 'Exportez', desc: 'Vérifiez, ajustez puis téléchargez sous Factur-X, Excel, JSON ou PDF certifié.' },
    },
    formats: {
      title: 'Formats acceptés',
      lede: 'Compatible avec tous les types de factures. Les PDF natifs comme les scans et photos sont traités avec précision.',
      items: [
        { label: 'PDF',   sub: 'Natif & numérisé',                icon: 'file-pdf' },
        { label: 'Image', sub: 'PNG · JPG · TIFF · BMP · HEIC',   icon: 'file-image' },
        { label: 'Word',  sub: '.docx · .doc',                    icon: 'file-doc' },
        { label: 'Excel', sub: '.xlsx · .xls',                    icon: 'file-sheet' },
      ],
    },
    exports: {
      title: 'Formats d\'export',
      lede: 'Pensé pour les experts-comptables et les logiciels d\'entreprise. Conforme UBL 2.1 et Factur-X (loi anti-fraude).',
      items: [
        { label: 'JSON',     sub: 'Données structurées',          icon: 'file-generic' },
        { label: 'CSV',      sub: 'Une ligne par article',        icon: 'file-sheet' },
        { label: 'XLSX',     sub: 'Feuille Excel complète',       icon: 'file-sheet' },
        { label: 'PDF',      sub: 'Facture harmonisée',           icon: 'file-pdf' },
        { label: 'UBL 2.1',  sub: 'XML conforme UE',              icon: 'file-doc' },
        { label: 'Factur-X', sub: 'PDF/A-3 hybride légal',        icon: 'file-pdf' },
        { label: 'TXT',      sub: 'Texte brut extrait',           icon: 'file-generic' },
      ],
    },
    security: {
      title: 'Conçu pour vos données sensibles',
      lede: 'Hébergement sur votre propre infrastructure. Vos documents ne quittent jamais votre environnement sécurisé.',
      points: [
        { icon: 'lock',   title: 'Chiffrement de bout en bout',  desc: 'Communications chiffrées en TLS et stockage sécurisé.' },
        { icon: 'shield', title: 'Authentification robuste',    desc: 'Mots de passe hashés avec Argon2id et jetons JWT sécurisés.' },
        { icon: 'eye',    title: 'Confidentialité totale',       desc: 'Aucune donnée privée n\'est transmise à des tiers.' },
        { icon: 'globe',  title: 'Conformité RGPD',              desc: 'Suppression définitive et en cascade sur simple demande.' },
      ],
    },
    pricing: {
      title: 'Tarifs transparents',
      lede: 'Installez gratuitement Ma Facture sur votre serveur ou profitez de nos formules clé en main.',
      tiers: [
        {
          name: 'Auto-hébergé',
          price: 'Gratuit',
          cadence: 'à vie',
          desc: 'Installez sur votre serveur Docker. Factures illimitées.',
          features: ['Factures illimitées', 'Tous les formats d\'export', 'Moteur OCR local', 'Mises à jour communautaires'],
          cta: 'Voir la documentation',
          featured: false,
        },
        {
          name: 'Starter',
          price: '19 €',
          cadence: '/ mois',
          desc: '500 factures / mois, 1 utilisateur.',
          features: ['500 factures / mois', '1 accès utilisateur', 'Sauvegardes automatiques', 'Support par email'],
          cta: 'Démarrer l\'essai',
          featured: true,
        },
        {
          name: 'Équipe',
          price: '89 €',
          cadence: '/ mois',
          desc: '5 000 factures / mois, 10 utilisateurs.',
          features: ['5 000 factures / mois', '10 accès utilisateurs', 'Gestion des rôles', 'Support prioritaire'],
          cta: 'Nous contacter',
          featured: false,
        },
      ],
    },
    cta: {
      title: 'Prêt à automatiser vos factures ?',
      lede: 'Glissez votre premier document et obtenez vos données structurées en quelques secondes.',
      primary: 'Créer un compte gratuit',
      secondary: 'Consulter le guide',
    },
    faq: {
      title: 'Questions fréquentes',
      items: [
        { q: 'Où s\'exécute la reconnaissance OCR ?', a: 'Directement dans votre conteneur local ou serveur, sans fuite de données.' },
        { q: 'L\'export Factur-X et UBL est-il certifié ?', a: 'Oui, les exports sont conformes aux normes européennes de facturation électronique.' },
        { q: 'Les photos prises depuis smartphone fonctionnent-elles ?', a: 'Absolument, les formats JPG, PNG et même HEIC (iPhone) sont convertis et reconnus.' },
        { q: 'Mes données sont-elles partagées ?', a: 'Jamais. Vous restez l\'unique propriétaire de vos données comptables.' },
      ],
    },
  },
  auth: {
    signin: 'Connexion',
    signup: 'Créer un compte',
    emailLabel: 'Adresse email',
    emailPlaceholder: 'vous@exemple.com',
    passwordLabel: 'Mot de passe',
    nameLabel: 'Nom complet',
    namePlaceholder: 'Jean Dupont',
    confirmLabel: 'Confirmer le mot de passe',
    acceptTerms: (terms: string, privacy: string) =>
      `J'ai lu et j'accepte les ${terms} et la ${privacy}.`,
    forgotPassword: 'Mot de passe oublié ?',
    submit: { signin: 'Se connecter', signup: 'Créer mon compte' },
    errors: {
      generic: 'Email ou mot de passe incorrect',
      weakPassword: 'Mot de passe trop court. Utilisez au moins 10 caractères.',
      mismatch: 'Les mots de passe ne correspondent pas.',
      mustAcceptTerms: 'Vous devez accepter les conditions d\'utilisation.',
      network: 'Serveur inaccessible. Veuillez réessayer.',
    },
    forgotTitle: 'Réinitialiser le mot de passe',
    forgotLede: 'Nous vous enverrons un lien si le compte existe.',
    forgotSent: 'Un lien de réinitialisation vous a été envoyé par email.',
  },
  dashboard: {
    nav: {
      overview: 'Tableau de bord',
      invoices: 'Factures',
      upload: 'Scan OCR',
      create: 'Nouvelle facture',
      archive: 'Archives',
      clients: 'Clients',
      settings: 'Paramètres',
      logout: 'Déconnexion',
    },
    list: {
      title: 'Factures',
      empty: { title: 'Aucune facture pour l\'instant', lede: 'Téléversez ou créez votre première facture pour commencer.' },
      cols: { date: 'Date', vendor: 'Fournisseur', number: 'Numéro', total: 'Total TTC', status: 'Statut', actions: 'Actions' },
      filter: { all: 'Tous les statuts', processing: 'En cours', ready: 'Validée', failed: 'Erreur', edited: 'Modifiée', draft: 'Brouillon' },
      search: 'Rechercher fournisseur, numéro…',
    },
    upload: {
      title: 'Scan OCR de factures',
      drop: 'Glissez vos factures ici',
      pick: 'ou cliquez pour sélectionner jusqu\'à 10 fichiers (PDF, images, Office jusqu\'à 25 Mo)',
      processing: 'Analyse OCR en cours…',
      ready: 'Terminé',
      failed: 'Échec',
    },
    detail: {
      title: 'Détail de la facture',
      sections: {
        vendor: 'Fournisseur (Émetteur)',
        buyer: 'Client (Destinataire)',
        invoice: 'Détails de la facture',
        totals: 'Totaux',
        items: 'Lignes d\'articles',
        notes: 'Conditions & notes',
      },
      export: 'Exporter',
      reprocess: 'Relancer l\'OCR',
      addLine: 'Ajouter une ligne',
      removeLine: 'Supprimer',
    },
    settings: {
      title: 'Paramètres',
      profile: 'Mon profil',
      password: 'Changer le mot de passe',
      danger: 'Zone de danger',
      deleteAccount: 'Supprimer mon compte',
      deleteConfirmTitle: 'Supprimer définitivement votre compte ?',
      deleteConfirmLede: 'Cette action supprimera toutes vos factures, données et clients. C\'est irréversible.',
      deleteTypePrompt: 'Tapez "supprimer" pour confirmer.',
    },
  },
  errors: {
    notFound: { title: 'Page introuvable', lede: 'La page demandée n\'existe pas.', back: 'Retour à l\'accueil' },
  },
} as const;

export type Dict = typeof en;
